import { FeatureName, useIsFeatureEnabled } from '@gripp/shared-logic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { RenderListItemPropsInterface } from 'react-native-dropdown-picker';
import { CheckboxOption, CheckboxState } from '../../crop-circle/Components/CheckboxOption';
import { tokens } from '../../crop-circle/tokens';
import { useFormContext } from '../../forms/formContext';
import { ControlledSelect } from '../../forms/select';
import AlertLogicInput from './alertLogicInput';
import AlertLogicPickerOld from './alertLogicPicker-old';
import { AlertOptions } from './alertOptions';
import {
  cropCircleDropdownStyles,
  getCropCircleDropDownPickerProps,
} from './cropCircleAlertDropdown';
import { AlertInputs, AlertType } from './types';

type AlertLogicPickerProps = {
  control: Control<any>;
  alertType: AlertType | undefined;
  secondAlertType: AlertType | undefined;
  additionalLogic: boolean;
  isCounterRoutine: boolean;
  counterRoutineItems: any[];
  showRoutinesDropdown: boolean;
  openDropdownOnLoad?: boolean;
  setAdditionalLogic: (value: boolean) => void;
  setShowRoutinesDropdown: (value: boolean) => void;
  onDropdownStateChange?: (isOpen: boolean) => void;
};

// TODO: Crop Circle Redesign: Remove after feature is fully rolled out
export default function AlertLogicPicker(props: AlertLogicPickerProps) {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  if (!isCropCircleRedesignEnabled) {
    return <AlertLogicPickerOld {...props} />;
  }
  return <AlertLogicPickerBase {...props} />;
}

function AlertLogicPickerBase({
  control,
  alertType,
  secondAlertType,
  additionalLogic,
  isCounterRoutine,
  counterRoutineItems,
  showRoutinesDropdown,
  openDropdownOnLoad,
  setShowRoutinesDropdown,
  setAdditionalLogic,
  onDropdownStateChange,
}: AlertLogicPickerProps) {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext();
  const [uomLabel, setUomLabel] = useState('');
  const [dropdownOpenCount, setDropdownOpenCount] = useState(0);
  const onDropdownStateChangeRef = useRef(onDropdownStateChange);
  
  useEffect(() => {
    onDropdownStateChangeRef.current = onDropdownStateChange;
  }, [onDropdownStateChange]);

  useEffect(() => {
    if (onDropdownStateChangeRef.current) {
      onDropdownStateChangeRef.current(dropdownOpenCount > 0);
    }
  }, [dropdownOpenCount]);

  const onDropdownOpen = useCallback(() => {
    setDropdownOpenCount((c) => c + 1);
  }, []);
  
  const onDropdownClose = useCallback(() => {
    setDropdownOpenCount((c) => Math.max(0, c - 1));
  }, []);

  const intervalRoutineId = getValues(AlertInputs.intervalRoutineId);

  useEffect(() => {
    if (!counterRoutineItems || counterRoutineItems.length === 0) return;
    const routineMatch = counterRoutineItems.find(
      (item) => item.value === intervalRoutineId
    );
    setUomLabel(routineMatch?.uom || '');
  }, [intervalRoutineId, counterRoutineItems]);

  const defaultDropdownItems = [
    { label: t('alerts.onDate'), value: AlertType.Date },
    { label: t('alerts.onRecurringInterval'), value: AlertType.RecurringInterval },
    { label: t('alerts.onInterval'), value: AlertType.Interval },
    { label: t('alerts.onDaysSkipped'), value: AlertType.DaysSkipped },
    { label: t('alerts.onRoutineCompletions'), value: AlertType.RoutineCompletions },
  ];

  const counterDropdownItems = [
    { label: t('alerts.onDate'), value: AlertType.Date },
    { label: t('alerts.onRecurringInterval'), value: AlertType.RecurringInterval },
    { label: t('alerts.onInterval'), value: AlertType.Interval },
    { label: t('alerts.minDropdownPlaceholder'), value: AlertType.Min },
    { label: t('alerts.maxDropdownPlaceholder'), value: AlertType.Max },
    { label: t('alerts.onDaysSkipped'), value: AlertType.DaysSkipped },
    { label: t('alerts.onRoutineCompletions'), value: AlertType.RoutineCompletions },
  ];

  const AlertItem = <T,>({
    label,
    isSelected,
    onPress,
    item,
  }: RenderListItemPropsInterface<T>) => {
    const handlePress = () => {
      const counterBasedTypes = [AlertType.Interval, AlertType.Min, AlertType.Max];
      if (counterBasedTypes.includes((item as any).value as AlertType)) {
        setShowRoutinesDropdown(true);
      }
      if ((item as any).type === AlertInputs.onIntervalStart) {
        setValue(
          AlertInputs.onIntervalStart,
          (item as any).count ? (item as any).count.toString() : undefined,
          { shouldDirty: true, shouldValidate: true }
        );
        setUomLabel((item as any).uom);
      }
      onPress(item as any);
    };
    const radioButtonValue: CheckboxState = isSelected ? 'checked' : 'unchecked';
    return (
      <Pressable onPress={handlePress} style={cropCircleDropdownStyles.listItem}>
        <Text style={cropCircleDropdownStyles.itemLabel}>{label}</Text>
        <CheckboxOption
          mode="radio"
          state={radioButtonValue}
          onPress={handlePress}
          style={{
            backgroundColor: 'transparent',
            padding: 0,
            borderRadius: 0,
            minHeight: 0,
          }}
        />
      </Pressable>
    );
  };

  const renderLogicInput = (
    dropdownName: string,
    selectedAlertType: AlertType | undefined,
    otherSelectedAlertType: AlertType | undefined,
    isPrimaryAlertInput: boolean
  ) => {
    const hasCounterRoutines = counterRoutineItems && counterRoutineItems.length > 0;
    const dropdownItems =
      isCounterRoutine || hasCounterRoutines ? counterDropdownItems : defaultDropdownItems;
    const filteredItems = dropdownItems.filter((item) => item.value !== otherSelectedAlertType);

    return (
      <>
        <ControlledSelect
          name={dropdownName}
          control={control}
          label={t('alerts.setAlertLogicPlaceholder')}
          items={filteredItems}
          listMode="SCROLLVIEW"
          dropDownPickerProps={getCropCircleDropDownPickerProps({
            openDropdownOnLoad: openDropdownOnLoad,
            renderListItem: (props) => <AlertItem {...props} />,
            onOpen: onDropdownOpen,
            onClose: onDropdownClose,
          })}
        />
        {[AlertType.Interval, AlertType.Min, AlertType.Max].includes(
          selectedAlertType as AlertType
        ) &&
          counterRoutineItems &&
          showRoutinesDropdown && (
            <ControlledSelect
              name={AlertInputs.intervalRoutineId}
              control={control}
              label={t('alerts.selectRoutine')}
              items={counterRoutineItems}
              listMode="SCROLLVIEW"
              dropDownPickerProps={getCropCircleDropDownPickerProps({
                style: [cropCircleDropdownStyles.dropdown, { marginTop: 8 }],
                openDropdownOnLoad: openDropdownOnLoad,
                renderListItem: (props) => <AlertItem {...props} />,
                onOpen: onDropdownOpen,
                onClose: onDropdownClose,
              })}
            />
          )}
        {selectedAlertType && counterRoutineItems && (
          <AlertLogicInput
            type={selectedAlertType}
            isPrimaryAlertInput={isPrimaryAlertInput}
            uomLabel={uomLabel || 'value'}
            setAdditionalLogic={(value) => setAdditionalLogic(value)}
            onDropdownOpen={onDropdownOpen}
            onDropdownClose={onDropdownClose}
          />
        )}
      </>
    );
  };

  return (
    <>
      <View style={styles.sectionContainer}>
        {/* Title "Set Alert Logic" is shown by parent (AlertFormBase) as section label */}
        {renderLogicInput('alertType', alertType, secondAlertType, true)}
      </View>

      {alertType && (
        <>
          <View style={styles.optionalSettingsContainer}>
            <View style={styles.lineBreak} />
            <Text style={styles.optionalSettingsLabel}>
              {t('alerts.optionalSettings')}
            </Text>
            <View style={styles.lineBreak} />
          </View>
          <View style={styles.sectionContainer}>
            <AlertOptions />
          </View>
        </>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  sectionContainer: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    backgroundColor: 'transparent',
    borderRadius: 0,
    overflow: 'visible' as const,
  },
  title: {
    color: tokens.colors.brand.black,
    ...tokens.typography.body.small,
    fontWeight: '600',
    marginBottom: tokens.spacing.sm,
  },
  optionalSettingsContainer: {
    height: 30,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 20,
  },
  lineBreak: {
    borderWidth: 0.5,
    maxHeight: 1,
    borderColor: tokens.colors.neutral[400],
    flex: 1,
  },
  optionalSettingsLabel: {
    fontSize: 14,
    fontWeight: '400',
    letterSpacing: -0.2,
    color: tokens.colors.system.textSecondary,
    marginHorizontal: 10,
  },
});
